import { DateTime } from "luxon";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SwitchComponent } from "@/Common/Components/Inputs/SwitchComponent";
import { Modal } from "@/Common/Components/Modals/Modal";
import { ObstetricalCounters, ObstetricalSummary } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { ObstetricalCountersComponent } from "../MedicalNote/ObstetricalFile/Control/ObstetricalCountersComponent";

import styles from "./ObstetricalSummaryPopup.module.scss";

interface IObstetricalSummaryPopupProps {
    obstetricalSummary: ObstetricalSummary;
    onSave: (obstetricalSummary: ObstetricalSummary) => void;
    onClose: () => void;
}

class ObstetricalSummaryPopupState {
    public obstetricalSummary: ObstetricalSummary;
}

class ObstetricalSummaryPopup extends BaseReactComponent<IObstetricalSummaryPopupProps, ObstetricalSummaryPopupState> {
    private componentId: string = "gtpals-form-popup";

    public constructor(props: IObstetricalSummaryPopupProps) {
        super(props);

        this.state = {
            obstetricalSummary: new ObstetricalSummary(props.obstetricalSummary)
        };
    }

    public render(): JSX.Element {
        if (!this.state)
            return null;

        return (
            <Modal
                id={this.componentId}
                onClose={() => this.props.onClose()}
                header={this.renderHeader()}
                footer={this.renderFooter()}
                width={1000}
                centerContent
                showOverlay
                disableOverlayClose
                roundTopCorner
                draggable
            >
                <ObstetricalCountersComponent
                    containerId={this.componentId}
                    renderHeaders
                    showAlternateAbortionCounter={cacheStore.ClinicSettings.EnableBritishColumbiaObstetricalFile && sessionStore.getCurrentEmployee().isActiveSiteBC()}
                    obstetricalCounters={this.state.obstetricalSummary}
                    onChange={(counters: ObstetricalCounters) => this.mutateState(s => s.obstetricalSummary = new ObstetricalSummary({ ...s, ...counters }))}
                />
            </Modal>
        );
    }

    private renderHeader(): JSX.Element {
        return (
            <div className={styles.header}>
                <div className={`${styles.title} dragHandle`}>
                    {cacheStore.GetString("PregnanciesLabel")}
                </div>
                <div className={styles.switchContainer}>
                    <SwitchComponent
                        componentId="gtpals-form-popup-switch"
                        onSwitchClick={() => this.mutateState(s => s.obstetricalSummary.IsPregnant = !s.obstetricalSummary.IsPregnant)}
                        checked={this.state.obstetricalSummary.IsPregnant}
                    />
                    <div>
                        <span style={{ fontSize: "large" }}>{cacheStore.GetString("PregnantLabel")}</span>
                    </div>
                </div>
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <div className="flex-container align-center">
                <div>
                    <span>{this.getLastModifiedString()}</span>
                </div>
                <div className={styles.footerButtons}>
                    <button className="btn btn-default" onClick={() => this.props.onClose()}>{cacheStore.GetString("CancelLabel")}</button>
                    <button className="btn btn-primary" onClick={() => this.props.onSave(this.state.obstetricalSummary)}>{cacheStore.GetString("SaveLabel")}</button>
                </div>
            </div>
        );
    }

    private getLastModifiedString(): string {
        let lastModifiedByString = "";
        const summary = this.state.obstetricalSummary;
        const lastUpdatedByEmployee = cacheStore.EmployeeCacheWithInactive.firstOrDefault(p => p.User_id == summary.LastUpdatedBy);

        if (lastUpdatedByEmployee) {
            const employeeTitle = cacheStore.GetLookupById(lastUpdatedByEmployee.PrefixLookupId)?.GetValue();
            const lastUpdatedByString = `${employeeTitle} ${lastUpdatedByEmployee.FirstName} ${lastUpdatedByEmployee.LastName}`;
            const lastUpdateDate = DateTime.fromISO(summary.LastUpdateDate.toString());
            const lastUpdatedDateString = `${cacheStore.GetString("OnLabel").toLowerCase()} ${lastUpdateDate.toFormat("yyyy-MM-dd")} ${cacheStore.GetString("AtLabel")} ${lastUpdateDate.toFormat("HH:mm:ss")}`;

            lastModifiedByString = `${cacheStore.GetString("LastModifiedByLabel")} ${lastUpdatedByString} ${lastUpdatedDateString}`;
        }

        return lastModifiedByString;
    }
}

export { ObstetricalSummaryPopup };
